import type { NextPage } from "next";
import Link from "next/link";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";

import { ArtBlocksGenerator } from "@/client/components/common/ArtBlocksGenerator/ArtBlocksGenerator";
import Button, {
  ButtonLoading,
} from "@/client/components/frame-design-system/buttons/Button";
import { Chevron } from "@/client/components/icons/chevron";
import analytics from "@/client/lib/analytics";
import { TOKEN_ID_GENERATION_VALUE } from "@/shared/artblocks";
import { Collection } from "@/shared/types/collection";

export type Props = {
  collection: Collection;
  tokenId?: string;
  hash?: string;
};

const ExplorePage: NextPage<Props> = ({ collection, tokenId, hash }: Props) => {
  const router = useRouter();

  const [status, setStatus] = useState<"loading" | "ready" | "error">(
    "loading"
  );

  const [tokenData, setTokenData] = useState<{
    tokenId: string;
    hash: string;
  } | null>(tokenId && hash ? { tokenId, hash } : null);

  // if no token data on load generate a random one
  useEffect(() => {
    randomizeToken();
  }, []);

  // if the token/hash changes up the URL
  useEffect(() => {
    updateUrlPath();
  }, [tokenData]);

  const randomizeToken = useCallback(() => {
    setStatus("loading");

    const { hash, tokenId } = randomTokenData(
      collection?.artBlocksProjectId
        ? Number(collection?.artBlocksProjectId)
        : 0,
      collection?.supplyMax ?? 0
    );
    setTokenData({
      tokenId,
      hash,
    });
  }, [collection?._id]);

  const updateUrlPath = () => {
    if (!tokenData?.tokenId || !tokenData?.hash) return;
    router.replace(
      {
        pathname: router.pathname,
        query: {
          id: router.query.id,
          tokenId: tokenData.tokenId,
          hash: tokenData.hash,
        },
      },
      undefined,
      {
        shallow: true,
      }
    );
  };

  const handleGenerateClicked = () => {
    randomizeToken();
    analytics.track("Collection Page - Generate OOB Button Clicked");
  };

  const handleGeneratorLoaded = () => {
    setStatus("ready");
  };

  return (
    <div>
      <div className="flex flex-col-reverse md:flex-row relative md:h-[calc(100vh_-_80px)] border-t border-neutral-200 dark:border-neutral-700">
        <div className="bg-white dark:bg-neutral-900 dark:border-neutral-700 dark:text-white border-r md:overflow-y-auto w-full md:w-[380px]">
          <div className="p-8">
            <div className="flex items-center gap-2 mb-8">
              <Link href={`/collections/${collection.slug || collection.id}`}>
                <span className="flex flex-col items-center justify-center flex-shrink-0 w-10 h-10 transition rounded-full hover:bg-neutral-200 dark:bg-neutral-800 dark:hover:bg-neutral-700 bg-neutral-100">
                  <Chevron size={24} className={`transform rotate-90`} />
                </span>
              </Link>
              <p className="text-sm">Back to Collection</p>
            </div>

            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              Exploring algorithm:
            </p>
            <h1 className="mt-3 text-2xl">{collection?.name}</h1>

            <div className="p-4 mt-8 text-sm rounded-md bg-neutral-100 dark:bg-neutral-800">
              <p className="mb-2 font-medium">
                Example Token #{tokenData?.tokenId}
              </p>
              <input
                disabled
                className="w-full p-2 border rounded-md dark:border-neutral-700 dark:text-neutral-300 dark:bg-neutral-800 text-neutral-600"
                value={tokenData?.hash || ""}
              />
            </div>

            <div className="mt-2">
              <Button onClick={handleGenerateClicked} className="w-full">
                {status === "loading" ? (
                  <ButtonLoading />
                ) : (
                  <span>Generate {collection?.name}</span>
                )}
              </Button>
            </div>

            <div className="p-2 mt-2 text-xs bg-orange-100 rounded-md dark:bg-orange-800">
              Some algorithms may take a long time to generate
            </div>

            <p className="mt-12 text-sm font-medium">What is this?</p>
            <p className="mt-1 text-sm dark:text-neutral-300">
              Algorithm Explorer lets you generate your own outputs using a
              collections script. This is useful to explore the possibilities
              available with an algorithm. But more importantly, it's fun! It's
              important to note however that these pieces are not real NFTs and
              are purely example outputs from the scripts.
            </p>
          </div>
        </div>

        <div
          style={{ minHeight: 350 }}
          className="relative flex flex-col items-center justify-center flex-grow h-full p-8 dark:bg-neutral-900 bg-neutral-100 sm:p-24"
        >
          {tokenData && collection?.artBlocksProjectId ? (
            <ArtBlocksGenerator
              contractAddress={collection.contractAddress}
              projectId={collection.artBlocksProjectId}
              aspectRatio={
                collection.aspectRatio
                  ? Number(collection.aspectRatio)
                  : undefined
              }
              tokenId={tokenData.tokenId}
              tokenHash={tokenData.hash}
              onLoaded={handleGeneratorLoaded}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

const randomTokenData = (projectIndex: number, maxInvocations: number) => {
  let hash = "0x";
  for (let i = 0; i < 64; i++) {
    hash += Math.floor(Math.random() * 16).toString(16);
  }

  return {
    hash,
    tokenId: `${
      TOKEN_ID_GENERATION_VALUE * projectIndex +
      Math.floor(Math.random() * maxInvocations)
    }`,
  };
};

export default ExplorePage;
